import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Loader2, Save, X, Eye, ExternalLink, AlertTriangle, CheckCircle, MapPin, Info, Star, CreditCard, Tag, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { useDashboardTab } from '@/context/DashboardTabContext';
import UnifiedPassPreview from './UnifiedPassPreview';
import {
  validateImageDimensions,
  validatePNGFormat,
  generateMultipleResolutions,
  getRecommendedDimensionsText,
  type ImageType,
  type ResizedImages
} from '@/lib/imageUtils';
import { isBackgroundDark } from '@/lib/core/utils';
import { deleteReplacedImages } from '@/lib/loyaltyImageCleanup';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface LoyaltyPassBuilderForm {
  // Apple Wallet Logo (rectangular OR square)
  appleLogoUrl: string;
  appleLogoFile?: File;
  appleLogoBase64?: string; // For draft persistence
  appleLogoType?: 'rectangular' | 'square'; // Track which format was uploaded

  // Google Wallet Logo (square only)
  googleLogoUrl: string;
  googleLogoFile?: File;
  googleLogoBase64?: string; // For draft persistence

  header: string;

  // Program Type and Settings
  programType: 'points' | 'stamps' | 'visits' | 'dotypos_points';

  // Top Row
  programName: string;
  loyaltyPointsLabel: string;
  memberLabel: string;
  loyaltyPointsType: 'int' | 'double';

  // Program-specific settings
  pointsForReward: number;
  pointsPerCurrency: number;
  stampsRequired: number;

  // Pass Appearance
  backgroundColor: string;
  heroImageUrl: string;
  heroImageFile?: File;
  heroImageBase64?: string; // For draft persistence

  // Location-based notifications
  locationNotificationsEnabled: boolean;
  locationNotificationMessage: string;
}

interface LoyaltyPassBuilderProps {
  businessId: string;
  editMode?: boolean;
  existingProgram?: any; // LoyaltyProgram type from LoyaltyProgramManager
  onComplete: (loyaltyProgram: any) => void;
  onCancel: () => void;
}

const LoyaltyPassBuilder: React.FC<LoyaltyPassBuilderProps> = ({
  businessId,
  editMode = false,
  existingProgram,
  onComplete,
  onCancel
}) => {
  const { setBusinessDashboardTab } = useDashboardTab();

  // localStorage key for draft data
  const DRAFT_KEY = editMode && existingProgram
    ? `loyaltyProgramEditDraft_${businessId}_${existingProgram.id}`
    : `loyaltyProgramDraft_${businessId}`;

  // Load draft from localStorage or use defaults
  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading loyalty draft:', error);
      return null;
    }
  };

  const draft = loadDraft();

  // Helper to convert base64 to File
  const base64ToFile = (base64: string, filename: string): File => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Initialize form data based on edit mode or draft
  const getInitialFormData = (): LoyaltyPassBuilderForm => {
    // If loading from draft (works for both create and edit mode), convert base64 back to File objects
    if (draft?.formData) {
      const draftData = draft.formData;
      return {
        ...draftData,
        appleLogoFile: draftData.appleLogoBase64 ? base64ToFile(draftData.appleLogoBase64, 'apple-logo.png') : undefined,
        googleLogoFile: draftData.googleLogoBase64 ? base64ToFile(draftData.googleLogoBase64, 'google-logo.png') : undefined,
        heroImageFile: draftData.heroImageBase64 ? base64ToFile(draftData.heroImageBase64, 'hero-image.png') : undefined
      };
    }

    if (editMode && existingProgram) {
      // Pre-fill with existing program data
      return {
        appleLogoUrl: existingProgram.apple_logo_urls?.['3x'] || '',
        appleLogoFile: undefined,
        appleLogoBase64: undefined,
        appleLogoType: existingProgram.apple_logo_urls ? 'square' : undefined,
        googleLogoUrl: existingProgram.google_logo_url || '',
        googleLogoFile: undefined,
        googleLogoBase64: undefined,
        header: existingProgram.description || '',
        programType: existingProgram.program_type || 'points',
        programName: existingProgram.name || '',
        loyaltyPointsLabel: existingProgram.loyalty_points_label || 'Reward Points',
        memberLabel: existingProgram.member_label || 'Member',
        loyaltyPointsType: 'int',
        pointsForReward: existingProgram.points_for_reward || 100,
        pointsPerCurrency: existingProgram.points_per_currency || 1,
        stampsRequired: existingProgram.stamps_required || 5,
        backgroundColor: existingProgram.background_color || '#72461d',
        heroImageUrl: existingProgram.hero_image_urls?.['3x'] || '',
        heroImageFile: undefined,
        heroImageBase64: undefined,
        locationNotificationsEnabled: existingProgram.location_notifications_enabled || false,
        locationNotificationMessage: existingProgram.location_notification_message || ''
      };
    }

    // Default values
    return {
      appleLogoUrl: '',
      appleLogoFile: undefined,
      appleLogoBase64: undefined,
      appleLogoType: undefined,
      googleLogoUrl: '',
      googleLogoFile: undefined,
      googleLogoBase64: undefined,
      header: '',
      programType: 'points',
      programName: '',
      loyaltyPointsLabel: 'Reward Points',
      memberLabel: 'Member',
      loyaltyPointsType: 'int',
      pointsForReward: 100,
      pointsPerCurrency: 1,
      stampsRequired: 5,
      backgroundColor: '#72461d',
      heroImageUrl: '',
      heroImageFile: undefined,
      heroImageBase64: undefined,
      locationNotificationsEnabled: false,
      locationNotificationMessage: ''
    };
  };

  const [formData, setFormData] = useState<LoyaltyPassBuilderForm>(getInitialFormData());

  // Dotypos connection state
  const [isDotyposConnected, setIsDotyposConnected] = useState(false);
  const [isCheckingDotypos, setIsCheckingDotypos] = useState(false);
  const [showDotyposConfirmDialog, setShowDotyposConfirmDialog] = useState(false);
  const [showPrivacyPolicyDialog, setShowPrivacyPolicyDialog] = useState(false);

  // Dotypos OAuth configuration
  const DOTYPOS_CLIENT_ID = 'tag-inn';
  const DOTYPOS_CLIENT_SECRET = 'um6MZ2by2wFa38njfj8e';
  const PASS_GENERATOR_URL = import.meta.env.VITE_PASS_GENERATOR_URL || 'https://pass.taginn.app';
  const REDIRECT_URI = `${PASS_GENERATOR_URL}/dotypos/callback`;

  const handleConnectToDotypos = () => {
    const authUrl = new URL('https://admin.dotykacka.cz/client/connect');
    authUrl.searchParams.append('client_id', DOTYPOS_CLIENT_ID);
    authUrl.searchParams.append('client_secret', DOTYPOS_CLIENT_SECRET);
    authUrl.searchParams.append('scope', '*');
    authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.append('state', businessId);
    window.open(authUrl.toString(), '_blank');
  };

  const [isCreating, setIsCreating] = useState(false);
  const [previewType, setPreviewType] = useState<'google' | 'apple'>(draft?.previewType || 'google');
  const [businessData, setBusinessData] = useState<any>(null);
  const [showLocationSettingsDialog, setShowLocationSettingsDialog] = useState(false);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragTarget, setDragTarget] = useState<'appleLogo' | 'googleLogo' | 'heroImage' | null>(null);
  const dragCounterRef = React.useRef(0);
  const dragTargetTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounce color updates to prevent lag
  const colorUpdateTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Update form data when existingProgram becomes available in edit mode
  // (but only if we don't have a draft - draft takes priority)
  useEffect(() => {
    if (editMode && existingProgram && !draft) {
      setFormData({
        appleLogoUrl: existingProgram.apple_logo_urls?.['3x'] || '',
        appleLogoFile: undefined,
        appleLogoBase64: undefined,
        appleLogoType: existingProgram.apple_logo_urls ? 'square' : undefined,
        googleLogoUrl: existingProgram.google_logo_url || '',
        googleLogoFile: undefined,
        googleLogoBase64: undefined,
        header: existingProgram.description || '',
        programType: existingProgram.program_type || 'points',
        programName: existingProgram.name || '',
        loyaltyPointsLabel: existingProgram.loyalty_points_label || 'Reward Points',
        memberLabel: existingProgram.member_label || 'Member',
        loyaltyPointsType: 'int',
        pointsForReward: existingProgram.points_for_reward || 100,
        pointsPerCurrency: existingProgram.points_per_currency || 1,
        stampsRequired: existingProgram.stamps_required || 5,
        backgroundColor: existingProgram.background_color || '#72461d',
        heroImageUrl: existingProgram.hero_image_urls?.['3x'] || '',
        heroImageFile: undefined,
        heroImageBase64: undefined
      });

    }
  }, [editMode, existingProgram]);

  // Save draft to localStorage whenever form data changes
  useEffect(() => {
    const draftData = {
      formData,
      previewType,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
    } catch (error) {
      console.error('Error saving loyalty draft:', error);
    }
  }, [formData, previewType, DRAFT_KEY]);

  // Clear draft on successful save or cancel
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('Error clearing loyalty draft:', error);
    }
  };

  // Helper function to get appropriate labels based on program type
  const getProgramTypeConfig = (type: 'points' | 'stamps' | 'visits' | 'dotypos_points') => {
    switch (type) {
      case 'points':
        return {
          label: 'Points',
          defaultLoyaltyLabel: 'Reward Points',
          icon: <Star className="h-4 w-4" />,
          description: 'Earn points with every purchase'
        };
      case 'dotypos_points':
        return {
          label: 'Dotypos Points',
          defaultLoyaltyLabel: 'Points',
          icon: <CreditCard className="h-4 w-4" />,
          description: 'Sync points with your Dotypos POS system'
        };
      case 'stamps':
        return {
          label: 'Stamps',
          defaultLoyaltyLabel: 'Stamps',
          icon: <Tag className="h-4 w-4" />,
          description: 'Collect stamps to earn rewards'
        };
      case 'visits':
        return {
          label: 'Visits',
          defaultLoyaltyLabel: 'Visits',
          icon: <Users className="h-4 w-4" />,
          description: 'Visit us to earn rewards'
        };
    }
  };

  // Handler for program type changes
  const handleProgramTypeChange = (newType: 'points' | 'stamps' | 'visits' | 'dotypos_points') => {
    const config = getProgramTypeConfig(newType);
    setFormData(prev => ({
      ...prev,
      programType: newType,
      loyaltyPointsLabel: config.defaultLoyaltyLabel
    }));
  };

  // Check Dotypos connection status
  useEffect(() => {
    const checkDotyposConnection = async () => {
      if (!businessId) return;

      try {
        setIsCheckingDotypos(true);
        const { data, error } = await supabase
          .from('businesses')
          .select('dotypos_enabled')
          .eq('id', businessId)
          .single();

        if (!error && data) {
          setIsDotyposConnected(data.dotypos_enabled || false);
        }
      } catch (error) {
        console.error('Error checking Dotypos connection:', error);
      } finally {
        setIsCheckingDotypos(false);
      }
    };

    checkDotyposConnection();
  }, [businessId]);

  // Load business data on mount (including location info)
  useEffect(() => {
    const loadBusinessData = async () => {
      try {
        const { data: business, error } = await supabase
          .from('businesses')
          .select('name, latitude, longitude, geofence_radius_meters')
          .eq('id', businessId)
          .single();

        if (!error && business) {
          setBusinessData(business);

          // Don't overwrite if we have draft data or in edit mode
          if (!draft && !editMode) {
            setFormData(prev => ({
              ...prev,
              header: business.name,
              programName: `${business.name} Rewards`,
              locationNotificationMessage: prev.locationNotificationMessage || `Welcome to ${business.name}!`
            }));
          }
        }
      } catch (error) {
        console.error('Error loading business data:', error);
      }
    };

    if (businessId) {
      loadBusinessData();
    }
  }, [businessId, editMode]);

  // Global drag handlers
  useEffect(() => {
    const handleGlobalDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setIsDragging(true);
      }
    };

    const handleGlobalDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
        setDragTarget(null);
      }
    };

    const handleGlobalDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleGlobalDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      setDragTarget(null);
    };

    document.addEventListener('dragenter', handleGlobalDragEnter);
    document.addEventListener('dragleave', handleGlobalDragLeave);
    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('drop', handleGlobalDrop);

    return () => {
      document.removeEventListener('dragenter', handleGlobalDragEnter);
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  const processImageFile = async (
    file: File,
    imageType: 'appleLogo' | 'googleLogo' | 'heroImage'
  ) => {
    // Validate file type - must be image
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file type', {
        description: 'Please upload an image file'
      });
      return false;
    }

    // Validate PNG format requirement
    const formatValidation = validatePNGFormat(file);
    if (!formatValidation.valid) {
      toast.error('Invalid file format', {
        description: formatValidation.message
      });
      return false;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', {
        description: 'Image must be smaller than 5MB'
      });
      return false;
    }

    // For Apple logo, try both rectangular and square formats
    if (imageType === 'appleLogo') {
      // Try rectangular first (480×150)
      const rectValidation = await validateImageDimensions(file, 'logo');

      if (rectValidation.valid) {
        // Rectangular logo - validated
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          setFormData(prev => ({
            ...prev,
            appleLogoFile: file,
            appleLogoBase64: base64,
            appleLogoType: 'rectangular',
            appleLogoUrl: ''
          }));
        };
        reader.readAsDataURL(file);

        toast.success('Apple logo ready', {
          description: 'Rectangular format (480×150) validated'
        });
        return true;
      }

      // Try square format (660×660)
      const squareValidation = await validateImageDimensions(file, 'logo_square');

      if (squareValidation.valid) {
        // Square logo - validated
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          setFormData(prev => ({
            ...prev,
            appleLogoFile: file,
            appleLogoBase64: base64,
            appleLogoType: 'square',
            appleLogoUrl: ''
          }));
        };
        reader.readAsDataURL(file);

        toast.success('Apple logo ready', {
          description: 'Square format (150×150) validated'
        });
        return true;
      }

      // Neither format matched
      toast.error('Invalid Apple logo dimensions', {
        description: `Image must be either 480 × 150 pixels (rectangular) or 150 × 150 pixels (square). Your image is ${rectValidation.actualWidth} × ${rectValidation.actualHeight} pixels.`
      });
      return false;
    }

    // For Google logo, only accept square (660×660)
    if (imageType === 'googleLogo') {
      const validation = await validateImageDimensions(file, 'logo_google_square');

      if (!validation.valid) {
        toast.error('Invalid Google logo dimensions', {
          description: validation.message
        });
        return false;
      }

      // Google logo - validated
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          googleLogoFile: file,
          googleLogoBase64: base64,
          googleLogoUrl: ''
        }));
      };
      reader.readAsDataURL(file);

      toast.success('Google logo ready', {
        description: 'Square format (660×660) validated'
      });
      return true;
    }

    // For hero/strip image
    if (imageType === 'heroImage') {
      const validation = await validateImageDimensions(file, 'strip');

      if (!validation.valid) {
        toast.error('Incorrect image dimensions', {
          description: validation.message
        });
        return false;
      }

      // Hero image - validated
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          heroImageFile: file,
          heroImageBase64: base64,
          heroImageUrl: ''
        }));
      };
      reader.readAsDataURL(file);

      toast.success('Hero image ready', {
        description: 'Strip format (1125×432) validated'
      });
      return true;
    }
    return false;
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    imageType: 'appleLogo' | 'googleLogo' | 'heroImage'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be selected again
    event.target.value = '';

    await processImageFile(file, imageType);
  };

  const removeImage = (imageType: 'appleLogo' | 'googleLogo' | 'heroImage') => {
    if (imageType === 'appleLogo') {
      setFormData(prev => ({
        ...prev,
        appleLogoFile: undefined,
        appleLogoBase64: undefined,
        appleLogoType: undefined,
        appleLogoUrl: ''
      }));
    } else if (imageType === 'googleLogo') {
      setFormData(prev => ({
        ...prev,
        googleLogoFile: undefined,
        googleLogoBase64: undefined,
        googleLogoUrl: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        heroImageFile: undefined,
        heroImageBase64: undefined,
        heroImageUrl: ''
      }));
    }
  };

  /**
   * Upload a single image file to Supabase storage
   */
  const uploadSingleImage = async (
    file: File,
    imageType: 'appleLogo' | 'googleLogo' | 'heroImage',
    resolution: '1x' | '2x' | '3x' = '3x'
  ): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const timestamp = Date.now();
    const fileName = `${businessId}_${imageType}_${resolution}_${timestamp}.${fileExt}`;

    console.log(`Uploading ${imageType} (${resolution}):`, fileName);

    try {
      // Upload to the existing business-assets bucket
      const { error: uploadError } = await supabase.storage
        .from('business-assets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('business-assets')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      console.log(`${imageType} (${resolution}) uploaded successfully:`, urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error) {
      console.error(`${imageType} (${resolution}) upload error:`, error);
      throw new Error(`${imageType} (${resolution}) upload failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Upload multiple resolution images for Apple Wallet
   * Generates resolutions from original file, then uploads all 3
   * Returns an object with URLs for all 3 resolutions
   */
  const uploadMultipleResolutions = async (
    originalFile: File,
    imageType: 'appleLogo' | 'heroImage',
    appleLogoType?: 'rectangular' | 'square'
  ): Promise<{ '1x': string; '2x': string; '3x': string }> => {
    try {
      // Determine the image type for generateMultipleResolutions
      let resolutionType: ImageType;
      if (imageType === 'appleLogo') {
        resolutionType = appleLogoType === 'rectangular' ? 'logo' : 'logo_square';
      } else {
        resolutionType = 'strip';
      }

      // Generate multiple resolutions from the original file
      const resizedImages = await generateMultipleResolutions(originalFile, resolutionType);

      // Upload all 3 resolutions in parallel
      const [url1x, url2x, url3x] = await Promise.all([
        uploadSingleImage(resizedImages['1x'], imageType, '1x'),
        uploadSingleImage(resizedImages['2x'], imageType, '2x'),
        uploadSingleImage(resizedImages['3x'], imageType, '3x')
      ]);

      return {
        '1x': url1x,
        '2x': url2x,
        '3x': url3x
      };
    } catch (error) {
      throw new Error(`Failed to upload multiple resolutions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const createGoogleLoyaltyProgram = async (): Promise<any> => {
    // Check if business has a privacy policy
    try {
      const { data: policyData, error: policyError } = await supabase
        .from('business_policies')
        .select('id')
        .eq('business_id', businessId)
        .maybeSingle();

      if (policyError && policyError.code !== 'PGRST116') {
        throw policyError;
      }

      if (!policyData) {
        setShowPrivacyPolicyDialog(true);
        throw new Error('Privacy policy required'); // Throw to stop execution
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'Privacy policy required') {
        throw error; // Re-throw to stop execution
      }
      console.error('Error checking business policy:', error);
      toast.error('Failed to verify privacy policy');
      throw error; // Throw to stop execution
    }

    try {
      // Upload images if provided
      let appleLogoUrls = null;
      let googleLogoUrl = formData.googleLogoUrl;
      let heroImageUrls = null;

      // Upload Apple logo (rectangular or square, multi-resolution)
      if (formData.appleLogoFile) {
        appleLogoUrls = await uploadMultipleResolutions(
          formData.appleLogoFile,
          'appleLogo',
          formData.appleLogoType
        );
      }

      // Upload Google logo (square only, single resolution)
      if (formData.googleLogoFile) {
        googleLogoUrl = await uploadSingleImage(formData.googleLogoFile, 'googleLogo', '3x');
      }

      // Upload hero/strip images (multi-resolution for Apple)
      if (formData.heroImageFile) {
        heroImageUrls = await uploadMultipleResolutions(
          formData.heroImageFile,
          'heroImage'
        );
      }

      // Google logo is required for Google Wallet
      if (!googleLogoUrl) {
        throw new Error('Google Wallet logo is required to create a loyalty program');
      }

      // 1. Create loyalty program in database
      const loyaltyProgramData: any = {
        business_id: businessId,
        name: formData.programName,
        description: formData.header,
        program_type: formData.programType,
        background_color: formData.backgroundColor,
        apple_logo_urls: appleLogoUrls, // Multi-resolution URLs for Apple Wallet (JSONB)
        google_logo_url: googleLogoUrl, // Single URL for Google Wallet
        hero_image_urls: heroImageUrls, // Multi-resolution strip images for Apple Wallet (JSONB)
        loyalty_points_label: formData.loyaltyPointsLabel,
        member_label: formData.memberLabel,
        is_active: true,
        location_notifications_enabled: formData.locationNotificationsEnabled,
        location_notification_message: formData.locationNotificationsEnabled ? formData.locationNotificationMessage : null,
        manual_notifications_enabled: true // Enable manual notifications by default
      };

      // Add program-specific fields
      if (formData.programType === 'points' || formData.programType === 'dotypos_points') {
        loyaltyProgramData.points_for_reward = formData.pointsForReward;
        loyaltyProgramData.points_per_currency = formData.pointsPerCurrency;
      } else if (formData.programType === 'stamps' || formData.programType === 'visits') {
        loyaltyProgramData.stamps_required = formData.stampsRequired;
      }

      const { data: loyaltyProgram, error: dbError } = await supabase
        .from('loyalty_programs')
        .insert(loyaltyProgramData)
        .select()
        .single();

      if (dbError) throw dbError;

      // Generate template code
      const templateCode = `${loyaltyProgram.id}_${Date.now()}`;

      // Fetch business coordinates for location notifications
      let businessLatitude: number | null = null;
      let businessLongitude: number | null = null;

      if (formData.locationNotificationsEnabled) {
        const { data: business, error: businessError } = await supabase
          .from('businesses')
          .select('latitude, longitude')
          .eq('id', businessId)
          .single();

        if (businessError) {
          console.warn('Could not fetch business coordinates for location notifications:', businessError);
        } else if (business?.latitude && business?.longitude) {
          businessLatitude = parseFloat(business.latitude.toString());
          businessLongitude = parseFloat(business.longitude.toString());
          console.log('📍 Business coordinates for location notifications:', { businessLatitude, businessLongitude });
        } else {
          console.warn('Business has no coordinates - location notifications will not work');
          toast.error("Warning", {
            description: "Business location not set. Location notifications will not work. Please add coordinates in Business Settings.",
          });
        }
      }

      // 2. Create Google Wallet Loyalty Class - Using exact Google format
      console.log('Creating Google Wallet loyalty class:', formData.programName);

      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://192.168.1.200:3001';

      // Build the exact Google Wallet loyalty class structure
      const googleClassData = {
        class_suffix: templateCode,
        class_data: {
          issuerName: formData.header,
          programName: formData.programName,
          programLogo: {
            sourceUri: {
              uri: googleLogoUrl
            },
            contentDescription: {
              defaultValue: {
                language: 'en-US',
                value: 'Program Logo'
              }
            }
          },
          hexBackgroundColor: formData.backgroundColor,
          reviewStatus: "UNDER_REVIEW",
          multipleDevicesAndHoldersAllowed: true,
          // Configure loyalty points structure - Google Wallet uses "loyaltyPoints" for all types
          loyaltyPoints: {
            label: formData.loyaltyPointsLabel,
            balance: {
              [formData.loyaltyPointsType]: 0  // Always start with 0
            }
          },
          textModulesData: [],
          // Add merchant locations for Google Wallet geofencing
          ...(formData.locationNotificationsEnabled && businessLatitude && businessLongitude && {
            merchantLocations: [{
              latitude: businessLatitude,
              longitude: businessLongitude
            }]
          })
        }
      };

      // Add hero image if provided
      if (heroImageUrls && heroImageUrls['3x']) {
        googleClassData.class_data.heroImage = {
          sourceUri: {
            uri: heroImageUrls['3x']
          },
          contentDescription: {
            defaultValue: {
              language: 'en-US',
              value: 'Hero Image'
            }
          }
        };
      }

      let googleClass = { id: `fallback_google_${templateCode}` };
      let googleError = null;

      try {
        const response = await fetch(`${passGeneratorUrl}/google-wallet/create-loyalty-class`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(googleClassData)
        });
        
        if (response.ok) {
          const result = await response.json();
          googleClass = { id: result.id || result.classId || `google_${templateCode}` };
          console.log('Google Wallet class created successfully:', googleClass.id);
        } else {
          const errorText = await response.text();
          googleError = new Error(`Google Wallet class creation failed: ${response.status} ${errorText}`);
          console.error('Google Wallet class creation error:', googleError);
        }
      } catch (error) {
        googleError = error;
        console.error('Google Wallet class creation failed:', error);
      }

      // 3. Generate QR code URL
      const baseUrl = 'https://taginn.app';
      const qrCodeData = `${baseUrl}/get-pass?template=${templateCode}`;
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeData)}`;

      // 4. Update loyalty program with generated template data
      const updateData: any = {
        template_code: templateCode,
        qr_code_url: qrCodeUrl,
        pass_distribution_url: qrCodeData,
        google_class_id: googleClass.id,
        apple_class_id: null // Google-only for now
      };

      const { error: updateError } = await supabase
        .from('loyalty_programs')
        .update(updateData)
        .eq('id', loyaltyProgram.id);

      if (updateError) throw updateError;

      // 5. Create corresponding pass_templates record
      const templateData = {
        qr: {
          size: "large", 
          enabled: true,
          position: "bottom"
        },
        fields: {
          headerFields: [],
          primaryFields: [
            {
              id: "1",
              key: formData.programType === 'points' ? 'points' : 'progress',
              type: "text", 
              label: formData.loyaltyPointsLabel,
              value: formData.programType === 'points' ? '0' : `0 / ${formData.stampsRequired}`
            }
          ],
          secondaryFields: [
            {
              id: "2",
              key: "member",
              type: "text",
              label: formData.memberLabel,
              value: "Customer Name"
            }
          ],
          auxiliaryFields: [],
          backFields: []
        }
      };

      const visualDesign = {
        logoText: formData.programName,
        labelColor: isBackgroundDark(formData.backgroundColor) ? '#cccccc' : '#333333',
        description: formData.header,
        backgroundColor: formData.backgroundColor,
        foregroundColor: isBackgroundDark(formData.backgroundColor) ? '#ffffff' : '#000000'
      };

      const { error: templateError } = await supabase
        .from('pass_templates')
        .insert({
          business_id: businessId,
          loyalty_program_id: loyaltyProgram.id,
          name: formData.programName,
          description: formData.header,
          template_code: templateCode,
          template_data: templateData,
          visual_design: visualDesign,
          custom_fields: [],
          is_active: true
        });

      if (templateError) {
        console.error('Error creating pass template:', templateError);
        // Don't fail the whole process if template creation fails
      }

      return { ...loyaltyProgram, ...updateData };

    } catch (error) {
      console.error('Error creating Google loyalty program:', error);
      throw error;
    }
  };

  const updateGoogleLoyaltyProgram = async () => {
    try {
      if (!existingProgram) {
        throw new Error('No existing program to update');
      }

      console.log('Updating loyalty program:', existingProgram.id);

      // 1. Handle image uploads (only if new images were selected)
      let appleLogoUrls = existingProgram.apple_logo_urls;
      let googleLogoUrl = existingProgram.google_logo_url;
      let heroImageUrls = existingProgram.hero_image_urls;

      // Upload new Apple logo if changed
      if (formData.appleLogoFile) {
        console.log('Uploading new Apple logo');
        appleLogoUrls = await uploadMultipleResolutions(
          formData.appleLogoFile,
          'appleLogo',
          formData.appleLogoType
        );
      }

      // Upload new Google logo if changed
      if (formData.googleLogoFile) {
        console.log('Uploading new Google logo');
        googleLogoUrl = await uploadSingleImage(
          formData.googleLogoFile,
          'googleLogo',
          '3x'
        );
      }

      // Upload new hero image if changed
      if (formData.heroImageFile) {
        console.log('Uploading new hero image');
        heroImageUrls = await uploadMultipleResolutions(
          formData.heroImageFile,
          'heroImage'
        );
      }

      // Delete old images that were replaced
      await deleteReplacedImages(existingProgram, {
        appleLogoUrls: formData.appleLogoFile ? appleLogoUrls : undefined,
        googleLogoUrl: formData.googleLogoFile ? googleLogoUrl : undefined,
        heroImageUrls: formData.heroImageFile ? heroImageUrls : undefined
      });

      // Fetch business coordinates for location notifications (for update)
      let businessLatitude: number | null = null;
      let businessLongitude: number | null = null;

      if (formData.locationNotificationsEnabled) {
        const { data: business, error: businessError } = await supabase
          .from('businesses')
          .select('latitude, longitude')
          .eq('id', businessId)
          .single();

        if (businessError) {
          console.warn('Could not fetch business coordinates for location notifications:', businessError);
        } else if (business?.latitude && business?.longitude) {
          businessLatitude = parseFloat(business.latitude.toString());
          businessLongitude = parseFloat(business.longitude.toString());
          console.log('📍 Business coordinates for location notifications (update):', { businessLatitude, businessLongitude });
        } else {
          console.warn('Business has no coordinates - location notifications will not work');
          toast.error("Warning", {
            description: "Business location not set. Location notifications will not work. Please add coordinates in Business Settings.",
          });
        }
      }

      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://192.168.1.200:3001';

      // 2. Update Google Wallet loyalty class
      const googleClassData = {
        class_suffix: existingProgram.template_code,
        class_data: {
          loyalty_program_id: existingProgram.id, // For updating pass objects
          loyaltyPointsLabel: formData.loyaltyPointsLabel, // For updating pass objects
          memberLabel: formData.memberLabel, // For updating pass objects
          issuerName: formData.header,
          programName: formData.programName,
          programLogo: {
            sourceUri: {
              uri: googleLogoUrl
            },
            contentDescription: {
              defaultValue: {
                language: 'en-US',
                value: 'Program Logo'
              }
            }
          },
          hexBackgroundColor: formData.backgroundColor,
          reviewStatus: "UNDER_REVIEW",
          multipleDevicesAndHoldersAllowed: true,
          loyaltyPoints: {
            label: formData.loyaltyPointsLabel,
            balance: {
              [formData.loyaltyPointsType]: 0
            }
          },
          textModulesData: [],
          // Add merchant locations for Google Wallet geofencing
          ...(formData.locationNotificationsEnabled && businessLatitude && businessLongitude && {
            merchantLocations: [{
              latitude: businessLatitude,
              longitude: businessLongitude
            }]
          })
        }
      };

      // Add hero image if provided
      if (heroImageUrls && heroImageUrls['3x']) {
        googleClassData.class_data.heroImage = {
          sourceUri: {
            uri: heroImageUrls['3x']
          },
          contentDescription: {
            defaultValue: {
              language: 'en-US',
              value: 'Hero Image'
            }
          }
        };
      }

      let googleUpdateError = null;

      try {
        const response = await fetch(`${passGeneratorUrl}/google-wallet/update-loyalty-class`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(googleClassData)
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Google Wallet class updated successfully:', result.id);
        } else {
          const errorText = await response.text();
          googleUpdateError = new Error(`Google Wallet class update failed: ${response.status} ${errorText}`);
          console.error('Google Wallet class update error:', googleUpdateError);
        }
      } catch (error) {
        googleUpdateError = error;
        console.error('Google Wallet class update failed:', error);
      }

      // 3. Update Apple Wallet pass template
      let appleUpdateError = null;

      try {
        console.log('Updating Apple Wallet pass template...');

        const appleTemplateUpdates = {
          backgroundColor: formData.backgroundColor,
          foregroundColor: isBackgroundDark(formData.backgroundColor) ? '#ffffff' : '#000000',
          labelColor: isBackgroundDark(formData.backgroundColor) ? '#cccccc' : '#333333',
          logoText: formData.header,
          organizationName: formData.programName,
          description: formData.header,
          loyaltyPointsLabel: formData.loyaltyPointsLabel,
          memberLabel: formData.memberLabel
        };

        const appleResponse = await fetch(`${passGeneratorUrl}/apple-wallet/update-loyalty-template`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            loyalty_program_id: existingProgram.id,
            template_updates: appleTemplateUpdates
          })
        });

        if (appleResponse.ok) {
          const appleResult = await appleResponse.json();
          console.log('Apple Wallet template updated successfully:', appleResult);

          // Log notification results
          if (appleResult.passesUpdated) {
            console.log(`Apple passes updated: ${appleResult.passesUpdated.updated}/${appleResult.passesUpdated.total}`);
            if (appleResult.passesUpdated.notifications) {
              console.log(`APNs notifications sent: ${appleResult.passesUpdated.notifications}`);
            }
          }
        } else {
          const errorText = await appleResponse.text();
          appleUpdateError = new Error(`Apple Wallet template update failed: ${appleResponse.status} ${errorText}`);
          console.error('Apple Wallet template update error:', appleUpdateError);
        }
      } catch (error) {
        appleUpdateError = error;
        console.error('Apple Wallet template update failed:', error);
      }

      // 4. Update database record
      const updateData: any = {
        name: formData.programName,
        description: formData.header,
        background_color: formData.backgroundColor,
        loyalty_points_label: formData.loyaltyPointsLabel,
        member_label: formData.memberLabel,
        apple_logo_urls: appleLogoUrls,
        google_logo_url: googleLogoUrl,
        hero_image_urls: heroImageUrls,
        location_notifications_enabled: formData.locationNotificationsEnabled,
        location_notification_message: formData.locationNotificationsEnabled ? formData.locationNotificationMessage : null,
        updated_at: new Date().toISOString()
      };

      // Update program-specific fields based on type (type itself is locked)
      if (formData.programType === 'points' || formData.programType === 'dotypos_points') {
        updateData.points_per_currency = formData.pointsPerCurrency;
        updateData.points_for_reward = formData.pointsForReward;
      } else {
        updateData.stamps_required = formData.stampsRequired;
      }

      const { data: updatedProgram, error: updateError } = await supabase
        .from('loyalty_programs')
        .update(updateData)
        .eq('id', existingProgram.id)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating loyalty program:', updateError);
        throw new Error(`Database update failed: ${updateError.message}`);
      }

      console.log('Loyalty program updated in database:', updatedProgram);

      // Show warnings if wallet updates failed but database succeeded
      if (googleUpdateError && appleUpdateError) {
        toast.error('Database updated but wallet updates failed', {
          description: 'Your changes are saved, but Google and Apple passes may not reflect updates immediately.'
        });
      } else if (googleUpdateError) {
        toast.error('Database updated but Google Wallet update failed', {
          description: 'Your changes are saved, but Google passes may not reflect updates immediately.'
        });
      } else if (appleUpdateError) {
        toast.error('Database updated but Apple Wallet update failed', {
          description: 'Your changes are saved, but Apple passes may not reflect updates immediately.'
        });
      }

      return updatedProgram;

    } catch (error) {
      console.error('Error updating Google loyalty program:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.header.trim()) {
      toast.error('Please enter a header for your loyalty program');
      return;
    }

    if (!formData.programName.trim()) {
      toast.error('Please enter a program name');
      return;
    }

    if (!formData.loyaltyPointsLabel.trim()) {
      toast.error(`Please enter a ${formData.programType === 'points' ? 'loyalty points' : formData.programType} label`);
      return;
    }

    // Validate all required images
    if (!formData.appleLogoUrl && !formData.appleLogoFile) {
      toast.error('Apple Wallet logo is required', {
        description: 'Please upload an Apple logo (480×150 rectangular or 150×150 square)'
      });
      return;
    }

    if (!formData.googleLogoUrl && !formData.googleLogoFile) {
      toast.error('Google Wallet logo is required', {
        description: 'Please upload a Google logo (660×660 square)'
      });
      return;
    }

    if (!formData.heroImageUrl && !formData.heroImageFile) {
      toast.error('Hero image is required', {
        description: 'Please upload a hero image (1125×432 for Apple Wallet)'
      });
      return;
    }

    setIsCreating(true);

    try {
      let loyaltyProgram;

      if (editMode) {
        // Update existing loyalty program
        loyaltyProgram = await updateGoogleLoyaltyProgram();
        toast.success('Loyalty program updated successfully!', {
          description: 'All customer passes will reflect the changes.'
        });
      } else {
        // Create new Google loyalty program
        loyaltyProgram = await createGoogleLoyaltyProgram();
        // Don't show toast here - parent component (LoyaltyProgramManager) will show success message
        clearDraft();
      }

      onComplete(loyaltyProgram);

    } catch (error) {
      console.error(editMode ? 'Error updating loyalty program:' : 'Error creating loyalty program:', error);

      if (error instanceof Error) {
        if (error.message.includes('upload failed')) {
          toast.error('Image upload failed', {
            description: 'Please check your image files and try again.'
          });
        } else if (error.message.includes('storage')) {
          toast.error('Storage error', {
            description: 'Please contact your administrator.'
          });
        } else {
          toast.error(editMode ? 'Failed to update loyalty program' : 'Failed to create loyalty program', {
            description: error.message
          });
        }
      } else {
        toast.error(editMode ? 'Failed to update loyalty program' : 'Failed to create loyalty program', {
          description: 'Please try again.'
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Global drag overlay - blurs everything except upload zones */}
      {isDragging && (
        <div className="fixed inset-0 z-[45] pointer-events-none">
          <div className="absolute inset-0 backdrop-blur-md bg-background/50" />
        </div>
      )}

      <div className="lg:w-1/2 lg:pr-4">
        {/* Left Panel - Form */}
        <Card className="h-fit">
        <CardHeader className="relative pb-3 flex-shrink-0">
          <div>
            <CardTitle className="text-sm font-medium">
              {editMode ? 'Edit Loyalty Pass' : 'Loyalty Pass Builder'}
            </CardTitle>
            <CardDescription>
              {editMode
                ? 'Update your loyalty pass design and settings. Changes will apply to all customer passes.'
                : 'Customize your loyalty pass design and settings for customers to save to their mobile wallets.'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { clearDraft(); onCancel(); }} className="absolute top-4 right-4 h-8 w-8 p-0">
            <span className="sr-only">Close</span>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Program Name */}
          <div>
            <Label htmlFor="programName">Program name</Label>
            <Input
              id="programName"
              value={formData.programName}
              onChange={(e) => setFormData(prev => ({ ...prev, programName: e.target.value }))}
              placeholder="Heraldic Rewards"
              className="mt-1"
            />
          </div>

          {/* Header */}
          <div>
            <Label htmlFor="header">Header</Label>
            <Input
              id="header"
              value={formData.header}
              onChange={(e) => setFormData(prev => ({ ...prev, header: e.target.value }))}
              placeholder="Heraldic Coffee"
              className="mt-1"
            />
          </div>

          {/* Program Type */}
          <div>
            <Label htmlFor="programType">Program Type {editMode && <span className="text-xs text-muted-foreground">(locked)</span>}</Label>
            <Select
              value={formData.programType}
              onValueChange={(value) => handleProgramTypeChange(value as 'points' | 'stamps' | 'visits' | 'dotypos_points')}
              disabled={editMode}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select program type" />
              </SelectTrigger>
              <SelectContent>
                {(['points', 'stamps', 'visits', 'dotypos_points'] as const).map((type) => {
                  const config = getProgramTypeConfig(type);
                  return (
                    <SelectItem key={type} value={type}>
                      <div className="flex items-center gap-2">
                        {config.icon}
                        <span>{config.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500 mt-2">
              {getProgramTypeConfig(formData.programType).description}
            </p>
          </div>

          {/* Dotypos Connection Status Alert */}
          {formData.programType === 'dotypos_points' && (
            <Alert className={isDotyposConnected ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}>
              <AlertDescription className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-2 flex-1">
                  {isDotyposConnected ? (
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    {isDotyposConnected ? (
                      <p className="text-sm text-green-800">
                        <strong>Dotypos Connected</strong> - Customers will be automatically synced to your POS system.
                      </p>
                    ) : (
                      <p className="text-sm text-yellow-800">
                        <strong>Dotypos Not Connected</strong> - Connect your Dotypos account to enable automatic customer syncing.
                      </p>
                    )}
                  </div>
                </div>
                {!isDotyposConnected && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowDotyposConfirmDialog(true)}
                    className="whitespace-nowrap"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Connect Now
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Logos side by side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Apple Wallet Logo */}
            <div className={`${isDragging ? 'relative z-50' : ''}`}>
              <Label htmlFor="apple-logo-upload" className={`${isDragging ? 'relative z-50' : ''}`}>
                Apple Wallet Logo <span className="text-red-500">*</span>
              </Label>
              <div
                className={`mt-1 border-2 border-dashed rounded-lg p-3 hover:border-primary/50 transition-all cursor-pointer ${
                  dragTarget === 'appleLogo' ? 'border-primary bg-primary/10 scale-105 shadow-lg' : ''
                } ${isDragging ? 'relative z-50 bg-background' : ''}`}
                onClick={() => document.getElementById('apple-logo-upload')?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                    dragTargetTimeoutRef.current = null;
                  }
                  setDragTarget('appleLogo');
                  // Don't stopPropagation - let it bubble to maintain global drag state
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  // Continuously reinforce the target state
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                    dragTargetTimeoutRef.current = null;
                  }
                  setDragTarget('appleLogo');
                  // Don't stopPropagation - let it bubble to maintain global drag state
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  // Very short debounce to avoid flicker
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                  }
                  dragTargetTimeoutRef.current = setTimeout(() => {
                    setDragTarget(null);
                    dragTargetTimeoutRef.current = null;
                  }, 10);
                  // Don't stopPropagation - let it bubble to maintain global drag state
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  // Don't stopPropagation - let it bubble to global drop handler to clear state
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    await processImageFile(file, 'appleLogo');
                  }
                }}
              >
                {(formData.appleLogoBase64 || formData.appleLogoUrl) ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={formData.appleLogoBase64 || formData.appleLogoUrl}
                      alt="Apple logo preview"
                      className="h-12 object-contain rounded"
                    />
                    <div className="flex-1 min-w-0" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage('appleLogo');
                      }}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-center py-1">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <div className="text-xs text-muted-foreground">
                      <p className="font-medium">Click or drop</p>
                      <p className="text-xs mt-0.5 font-medium">480×150 or 150×150</p>
                      <p className="text-[10px] mt-0.5">PNG required</p>
                    </div>
                  </div>
                )}
              </div>
              <input
                id="apple-logo-upload"
                type="file"
                accept="image/png"
                onChange={(e) => handleImageUpload(e, 'appleLogo')}
                className="hidden"
              />
            </div>

            {/* Google Wallet Logo */}
            <div className={`${isDragging ? 'relative z-50' : ''}`}>
              <Label htmlFor="google-logo-upload" className={`${isDragging ? 'relative z-50' : ''}`}>
                Google Wallet Logo <span className="text-red-500">*</span>
              </Label>
              <div
                className={`mt-1 border-2 border-dashed rounded-lg p-3 hover:border-primary/50 transition-all cursor-pointer ${
                  dragTarget === 'googleLogo' ? 'border-primary bg-primary/10 scale-105 shadow-lg' : ''
                } ${isDragging ? 'relative z-50 bg-background' : ''}`}
                onClick={() => document.getElementById('google-logo-upload')?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                    dragTargetTimeoutRef.current = null;
                  }
                  setDragTarget('googleLogo');
                  // Don't stopPropagation - let it bubble to maintain global drag state
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  // Continuously reinforce the target state
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                    dragTargetTimeoutRef.current = null;
                  }
                  setDragTarget('googleLogo');
                  // Don't stopPropagation - let it bubble to maintain global drag state
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  // Very short debounce to avoid flicker
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                  }
                  dragTargetTimeoutRef.current = setTimeout(() => {
                    setDragTarget(null);
                    dragTargetTimeoutRef.current = null;
                  }, 10);
                  // Don't stopPropagation - let it bubble to maintain global drag state
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  // Don't stopPropagation - let it bubble to global drop handler to clear state
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    await processImageFile(file, 'googleLogo');
                  }
                }}
              >
                {(formData.googleLogoBase64 || formData.googleLogoUrl) ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={formData.googleLogoBase64 || formData.googleLogoUrl}
                      alt="Google logo preview"
                      className="h-12 w-12 object-contain rounded"
                    />
                    <div className="flex-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage('googleLogo');
                      }}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-center py-1">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <div className="text-xs text-muted-foreground">
                      <p className="font-medium">Click or drop</p>
                      <p className="text-xs mt-0.5 font-medium">660×660</p>
                      <p className="text-[10px] mt-0.5">PNG required</p>
                    </div>
                  </div>
                )}
              </div>
              <input
                id="google-logo-upload"
                type="file"
                accept="image/png"
                onChange={(e) => handleImageUpload(e, 'googleLogo')}
                className="hidden"
              />
            </div>
          </div>

          {/* Hero Image - Full width below logos */}
          <div className={`${isDragging ? 'relative z-50' : ''}`}>
            <Label htmlFor="hero-image-upload" className={`${isDragging ? 'relative z-50' : ''}`}>
              Hero image <span className="text-red-500">*</span>
            </Label>
            <div
              className={`mt-1 border-2 border-dashed rounded-lg p-3 hover:border-primary/50 transition-all cursor-pointer ${
                dragTarget === 'heroImage' ? 'border-primary bg-primary/10 scale-[1.02] shadow-lg' : ''
              } ${isDragging ? 'relative z-50 bg-background' : ''}`}
              onClick={() => document.getElementById('hero-image-upload')?.click()}
              onDragEnter={(e) => {
                e.preventDefault();
                if (dragTargetTimeoutRef.current) {
                  clearTimeout(dragTargetTimeoutRef.current);
                  dragTargetTimeoutRef.current = null;
                }
                setDragTarget('heroImage');
                // Don't stopPropagation - let it bubble to maintain global drag state
              }}
              onDragOver={(e) => {
                e.preventDefault();
                // Continuously reinforce the target state
                if (dragTargetTimeoutRef.current) {
                  clearTimeout(dragTargetTimeoutRef.current);
                  dragTargetTimeoutRef.current = null;
                }
                setDragTarget('heroImage');
                // Don't stopPropagation - let it bubble to maintain global drag state
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                // Very short debounce to avoid flicker
                if (dragTargetTimeoutRef.current) {
                  clearTimeout(dragTargetTimeoutRef.current);
                }
                dragTargetTimeoutRef.current = setTimeout(() => {
                  setDragTarget(null);
                  dragTargetTimeoutRef.current = null;
                }, 10);
                // Don't stopPropagation - let it bubble to maintain global drag state
              }}
              onDrop={async (e) => {
                e.preventDefault();
                // Don't stopPropagation - let it bubble to global drop handler to clear state
                const file = e.dataTransfer.files[0];
                if (file) {
                  await processImageFile(file, 'heroImage');
                }
              }}
            >
              {(formData.heroImageBase64 || formData.heroImageUrl) ? (
                <div className="flex items-center gap-3">
                  <img
                    src={formData.heroImageBase64 || formData.heroImageUrl}
                    alt="Hero image preview"
                    className="h-16 w-auto object-contain rounded"
                  />
                  <div className="flex-1" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage('heroImage');
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 text-center py-1.5">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium">Click or drop</p>
                    <p className="text-xs mt-0.5 font-medium">1125×432</p>
                    <p className="text-[10px] mt-0.5">PNG required</p>
                  </div>
                </div>
              )}
            </div>
            <input
              id="hero-image-upload"
              type="file"
              accept="image/png"
              onChange={(e) => handleImageUpload(e, 'heroImage')}
              className="hidden"
            />
          </div>

          {/* Background Color */}
          <div>
            <Label htmlFor="backgroundColor">Background color</Label>
            <div className="flex items-center gap-2 mt-1">
              {/* Color Picker Preview */}
              <input
                id="backgroundColorPicker"
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => {
                  const newColor = e.target.value;

                  // Clear any pending timeout
                  if (colorUpdateTimeoutRef.current) {
                    clearTimeout(colorUpdateTimeoutRef.current);
                  }

                  // Debounce the update - only update after user stops dragging for 150ms
                  colorUpdateTimeoutRef.current = setTimeout(() => {
                    setFormData(prev => ({ ...prev, backgroundColor: newColor }));
                  }, 150);
                }}
                className="w-10 h-10 rounded border cursor-pointer"
                aria-label="Background color picker"
              />

              {/* Hex Input */}
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">Hex:</span>
                <Input
                  id="backgroundColor"
                  value={formData.backgroundColor}
                  onChange={(e) => {
                    const hex = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                      setFormData(prev => ({ ...prev, backgroundColor: hex }));
                    }
                  }}
                  className="font-mono w-24 h-9"
                  placeholder="#72461d"
                  maxLength={7}
                />
              </div>

              {/* RGB Inputs */}
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">R:</span>
                <Input
                  type="number"
                  min="0"
                  max="255"
                  value={parseInt(formData.backgroundColor.slice(1, 3), 16) || 0}
                  onChange={(e) => {
                    const r = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                    const g = parseInt(formData.backgroundColor.slice(3, 5), 16) || 0;
                    const b = parseInt(formData.backgroundColor.slice(5, 7), 16) || 0;
                    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                    setFormData(prev => ({ ...prev, backgroundColor: hex }));
                  }}
                  className="w-16 h-9"
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">G:</span>
                <Input
                  type="number"
                  min="0"
                  max="255"
                  value={parseInt(formData.backgroundColor.slice(3, 5), 16) || 0}
                  onChange={(e) => {
                    const r = parseInt(formData.backgroundColor.slice(1, 3), 16) || 0;
                    const g = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                    const b = parseInt(formData.backgroundColor.slice(5, 7), 16) || 0;
                    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                    setFormData(prev => ({ ...prev, backgroundColor: hex }));
                  }}
                  className="w-16 h-9"
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">B:</span>
                <Input
                  type="number"
                  min="0"
                  max="255"
                  value={parseInt(formData.backgroundColor.slice(5, 7), 16) || 0}
                  onChange={(e) => {
                    const r = parseInt(formData.backgroundColor.slice(1, 3), 16) || 0;
                    const g = parseInt(formData.backgroundColor.slice(3, 5), 16) || 0;
                    const b = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                    setFormData(prev => ({ ...prev, backgroundColor: hex }));
                  }}
                  className="w-16 h-9"
                />
              </div>
            </div>
          </div>

          {/* Loyalty Points Label and Member Label - Side by Side */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="loyaltyPointsLabel">
                {formData.programType === 'points' ? 'Loyalty points label' :
                 formData.programType === 'stamps' ? 'Stamps label' : 'Visits label'}
              </Label>
              <Input
                id="loyaltyPointsLabel"
                value={formData.loyaltyPointsLabel}
                onChange={(e) => setFormData(prev => ({ ...prev, loyaltyPointsLabel: e.target.value }))}
                placeholder={getProgramTypeConfig(formData.programType).defaultLoyaltyLabel}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="memberLabel">Member label</Label>
              <Input
                id="memberLabel"
                value={formData.memberLabel}
                onChange={(e) => setFormData(prev => ({ ...prev, memberLabel: e.target.value }))}
                placeholder="Member"
                className="mt-1"
              />
            </div>
          </div>

          {/* Program-specific settings */}
          {formData.programType === 'points' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pointsPerCurrency">Points per $1</Label>
                <Input
                  id="pointsPerCurrency"
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={formData.pointsPerCurrency}
                  onChange={(e) => setFormData(prev => ({ ...prev, pointsPerCurrency: parseFloat(e.target.value) || 1 }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="pointsForReward">Points for reward</Label>
                <Input
                  id="pointsForReward"
                  type="number"
                  min="1"
                  value={formData.pointsForReward}
                  onChange={(e) => setFormData(prev => ({ ...prev, pointsForReward: parseInt(e.target.value) || 100 }))}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {(formData.programType === 'stamps' || formData.programType === 'visits') && (
            <div>
              <Label htmlFor="stampsRequired">
                {formData.programType === 'stamps' ? 'Stamps required' : 'Visits required'}
              </Label>
              <Input
                id="stampsRequired"
                type="number"
                min="1"
                value={formData.stampsRequired}
                onChange={(e) => setFormData(prev => ({ ...prev, stampsRequired: parseInt(e.target.value) || 5 }))}
                className="mt-1"
              />
              <p className="text-sm text-gray-500 mt-1">
                How many {formData.programType} needed to earn the reward
              </p>
            </div>
          )}

          {/* Location-Based Notifications */}
          <div className="pt-4 space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  <Label htmlFor="locationNotifications" className="text-sm font-semibold">
                    Location-Based Notifications
                  </Label>
                  <TooltipProvider>
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <button type="button" className="inline-flex items-center">
                          <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs" side="right">
                        <p className="text-sm">
                          When enabled, the pass will appear on customers' lock screens when they are near your business location.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Switch
                  id="locationNotifications"
                  checked={formData.locationNotificationsEnabled}
                  onCheckedChange={(checked) => {
                    if (checked && (!businessData?.latitude || !businessData?.longitude)) {
                      setShowLocationSettingsDialog(true);
                      return;
                    }
                    setFormData(prev => ({ ...prev, locationNotificationsEnabled: checked }));
                  }}
                />
              </div>

              {businessData?.latitude && businessData?.longitude && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Location: {businessData.latitude.toFixed(4)}, {businessData.longitude.toFixed(4)} • Radius: {businessData.geofence_radius_meters || 100}m
                </div>
              )}
            </div>

            {formData.locationNotificationsEnabled && (
              <div>
                <Label htmlFor="locationMessage">Relevant Text (Lock Screen Message)</Label>
                <Textarea
                  id="locationMessage"
                  value={formData.locationNotificationMessage}
                  onChange={(e) => setFormData(prev => ({ ...prev, locationNotificationMessage: e.target.value }))}
                  placeholder={`Welcome to ${businessData?.name || 'our store'}!`}
                  maxLength={200}
                  rows={2}
                  className="mt-1 resize-none"
                />
                <p className="text-sm text-gray-500 mt-1">
                  This message appears on the lock screen when customers are within {businessData?.geofence_radius_meters || 100} meters of your business.
                  Maximum 200 characters.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {formData.locationNotificationMessage.length}/200 characters
                </p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => { clearDraft(); onCancel(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isCreating}
              className="flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {editMode ? 'Updating Program...' : 'Creating Program...'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editMode ? 'Update Loyalty Program' : 'Create Loyalty Program'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Right Panel - Live Preview - 50% width for symmetry */}
      <div className="lg:fixed lg:top-32 lg:right-0 lg:w-1/2 space-y-3 mt-8 lg:mt-0 lg:pl-4">
        <UnifiedPassPreview
          key={`${formData.programType}-${formData.header}-${formData.appleLogoBase64}-${formData.googleLogoBase64}-${formData.heroImageBase64}`}
          data={{
            businessName: formData.header || 'Business Name',
            programName: formData.programName || 'Loyalty Program',
            programType: formData.programType,
            loyaltyPointsLabel: formData.loyaltyPointsLabel,
            memberLabel: formData.memberLabel,
            backgroundColor: formData.backgroundColor,
            heroImageUrl: formData.heroImageBase64 || formData.heroImageUrl,
            requiredCount: formData.programType === 'points' ? formData.pointsForReward : formData.stampsRequired
          }}
          passData={{
            google_logo_url: formData.googleLogoBase64 || formData.googleLogoUrl,
            apple_logo_urls: formData.appleLogoBase64 || formData.appleLogoUrl ? {
              '1x': formData.appleLogoBase64 || formData.appleLogoUrl,
              '2x': formData.appleLogoBase64 || formData.appleLogoUrl,
              '3x': formData.appleLogoBase64 || formData.appleLogoUrl
            } : undefined,
            hero_image_urls: formData.heroImageBase64 || formData.heroImageUrl ? {
              '1x': formData.heroImageBase64 || formData.heroImageUrl,
              '2x': formData.heroImageBase64 || formData.heroImageUrl,
              '3x': formData.heroImageBase64 || formData.heroImageUrl
            } : undefined
          }}
          mode="creation"
          showPlatformToggle={false}
          heroImagePreview={formData.heroImageBase64}
          customerName="John Doe"
          onBusinessNameClick={() => {
            const headerInput = document.getElementById('header');
            if (headerInput) {
              headerInput.focus();
              headerInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          onAppleLogoClick={() => {
            const uploadArea = document.getElementById('apple-logo-upload')?.parentElement;
            if (uploadArea) {
              uploadArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the upload area briefly
              uploadArea.classList.add('ring-2', 'ring-primary');
              setTimeout(() => {
                uploadArea.classList.remove('ring-2', 'ring-primary');
              }, 2000);
            }
          }}
          onGoogleLogoClick={() => {
            const uploadArea = document.getElementById('google-logo-upload')?.parentElement;
            if (uploadArea) {
              uploadArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the upload area briefly
              uploadArea.classList.add('ring-2', 'ring-primary');
              setTimeout(() => {
                uploadArea.classList.remove('ring-2', 'ring-primary');
              }, 2000);
            }
          }}
          onProgramNameClick={() => {
            const programNameInput = document.getElementById('programName');
            if (programNameInput) {
              programNameInput.focus();
              programNameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          onLoyaltyPointsLabelClick={() => {
            const loyaltyLabelInput = document.getElementById('loyaltyPointsLabel');
            if (loyaltyLabelInput) {
              loyaltyLabelInput.focus();
              loyaltyLabelInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          onMemberLabelClick={() => {
            const memberLabelInput = document.getElementById('memberLabel');
            if (memberLabelInput) {
              memberLabelInput.focus();
              memberLabelInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          onHeroImageClick={() => {
            const uploadArea = document.getElementById('hero-image-upload')?.parentElement;
            if (uploadArea) {
              uploadArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the upload area briefly
              uploadArea.classList.add('ring-2', 'ring-primary');
              setTimeout(() => {
                uploadArea.classList.remove('ring-2', 'ring-primary');
              }, 2000);
            }
          }}
        />
      </div>
      </div>

      {/* Dotypos Settings Navigation Confirmation Dialog */}
      <AlertDialog open={showDotyposConfirmDialog} onOpenChange={setShowDotyposConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Go to Settings?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will take you to the <strong>Settings tab</strong> where you can connect your Dotypos POS system.
              </p>
              <p className="text-sm text-muted-foreground">
                Your loyalty program progress will be auto-saved.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // Set a flag in sessionStorage to trigger Dotypos highlighting
                sessionStorage.setItem('highlightDotypos', 'true');
                setBusinessDashboardTab('settings');
                setShowDotyposConfirmDialog(false);
              }}
            >
              Go to Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Privacy Policy Navigation Confirmation Dialog */}
      {/* Location Settings Required Dialog */}
      <AlertDialog open={showLocationSettingsDialog} onOpenChange={setShowLocationSettingsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Business Location Required</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  You need to set your business location before enabling location-based notifications.
                </p>
                <p>
                  Go to the <strong>Settings tab</strong> to add your business location?
                </p>
                <p className="text-sm text-muted-foreground">
                  Your loyalty program progress will be auto-saved.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // Set a flag in sessionStorage to trigger location settings highlighting
                sessionStorage.setItem('highlightLocation', 'true');
                setBusinessDashboardTab('settings');
                setShowLocationSettingsDialog(false);
              }}
            >
              Go to Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Privacy Policy Required Dialog */}
      <AlertDialog open={showPrivacyPolicyDialog} onOpenChange={setShowPrivacyPolicyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Privacy Policy Required</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  You need to add a privacy policy before creating loyalty programs.
                </p>
                <p>
                  Go to the <strong>Settings tab</strong> to add your privacy policy?
                </p>
                <p className="text-sm text-muted-foreground">
                  Your loyalty program progress will be auto-saved.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // Set a flag in sessionStorage to trigger privacy policy highlighting
                sessionStorage.setItem('highlightPrivacyPolicy', 'true');
                setBusinessDashboardTab('settings');
                setShowPrivacyPolicyDialog(false);
              }}
            >
              Go to Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoyaltyPassBuilder;